import React, { useEffect, useState } from 'react';
import { useLocale } from '../i18n';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const POLL_INTERVAL_MS = 10000;

interface WorkerInfo {
  worker_id: string;
  model: string;
  gtp_config: string;
  executable: string;
  last_seen: number;
  idle_ms: number;
}

interface HealthData {
  status: 'ok' | 'no_workers';
  queue_length: number;
  pending_jobs: number;
  workers: WorkerInfo[];
}

const SystemStatus: React.FC = () => {
  const { t } = useLocale();
  const [health, setHealth] = useState<HealthData | null>(null);
  const [expanded, setExpanded] = useState(false);

  // Close on ESC or click outside
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpanded(false); };
    const onClickOutside = (e: MouseEvent) => {
      const panel = document.getElementById('system-status-panel');
      if (panel && !panel.contains(e.target as Node)) setExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClickOutside);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClickOutside);
    };
  }, [expanded]);

  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    const fetchHealth = async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/health`);
        const data = await resp.json();
        setHealth({ workers: [], ...data });
        setLastUpdated(new Date());
      } catch {
        setHealth(null);
      }
    };

    fetchHealth();
    const interval = setInterval(fetchHealth, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const isOk = health?.status === 'ok';
  const dotColor = health === null ? '#aaa' : isOk ? '#4CAF50' : '#dc3545';
  const dotTitle = health === null ? t('gatewayUnreachable') : isOk ? t('systemHealthy') : t('noWorkersConnected');

  const filename = (path: string) => path.replace(/\\/g, '/').split('/').pop() || path;

  return (
    <div style={{ position: 'relative', display: 'inline-block', zIndex: 1000 }}>

      {/* Status dot — always visible */}
      <div
        onClick={() => setExpanded(e => !e)}
        title={dotTitle}
        style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: dotColor,
          cursor: 'pointer',
          boxShadow: `0 0 6px ${dotColor}`,
        }}
      />

      {/* Expanded panel */}
      {expanded && (
        <div id="system-status-panel" style={{
          position: 'absolute',
          bottom: '20px',
          right: '0',
          backgroundColor: '#1e1e1e',
          color: '#d4d4d4',
          borderRadius: '8px',
          padding: '12px 16px',
          minWidth: '320px',
          fontSize: '12px',
          fontFamily: 'monospace',
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          border: '1px solid #333',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#fff' }}>{t('systemStatus')}</span>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <span style={{ color: '#666', fontSize: '11px' }}>
                {lastUpdated ? `${t('updatedAt')} ${lastUpdated.toLocaleTimeString()}` : ''}
              </span>
              <span
                onClick={() => setExpanded(false)}
                style={{ color: '#666', cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}
                title={t('close')}
              >×</span>
            </div>
          </div>

          {health === null ? (
            <div style={{ color: '#dc3545' }}>{t('gatewayUnreachable')}</div>
          ) : (
            <>
              {/* Gateway */}
              <div style={{ marginBottom: '10px' }}>
                <div style={{ color: '#888', marginBottom: '4px' }}>{t('gateway')}</div>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <span>{t('queue')} <span style={{ color: health.queue_length > 5 ? '#f0a500' : '#4CAF50' }}>{health.queue_length}</span></span>
                  <span>{t('pending')} <span style={{ color: '#d4d4d4' }}>{health.pending_jobs}</span></span>
                  <span>{t('statusLabel')} <span style={{ color: isOk ? '#4CAF50' : '#dc3545' }}>{health.status}</span></span>
                </div>
              </div>

              {/* Workers */}
              <div>
                <div style={{ color: '#888', marginBottom: '4px' }}>
                  {t('workersLabel')} ({health.workers.length} {t('activeLabel')})
                </div>
                {health.workers.length === 0 ? (
                  <div style={{ color: '#dc3545' }}>{t('noWorkersConnected')}</div>
                ) : (
                  health.workers.map(w => (
                    <div key={w.worker_id} style={{
                      borderLeft: '2px solid #4CAF50',
                      paddingLeft: '8px',
                      marginBottom: '8px',
                    }}>
                      <div style={{ color: '#fff', marginBottom: '2px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span>{t('workerLabel')} <span style={{ color: '#4CAF50' }}>{w.worker_id}</span></span>
                        <span style={{ color: '#666', fontSize: '11px' }}>
                          {w.idle_ms < 15000 ? `● ${t('activeLabel')}` : `${t('idleLabel')} ${Math.round(w.idle_ms / 1000)}s`}
                        </span>
                      </div>
                      <div style={{ color: '#888' }}>{t('modelLabel')}  <span style={{ color: '#ce9178' }}>{filename(w.model)}</span></div>
                      <div style={{ color: '#888' }}>{t('configLabel')} <span style={{ color: '#ce9178' }}>{filename(w.gtp_config)}</span></div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SystemStatus;
